import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { listAuditLogsDirect } from '@timetable/api';
import type { AuditLogEntry } from '@timetable/types';

const OPERATION_COLORS = {
  create: 'success',
  update: 'info',
  delete: 'error',
} as const;

const OPERATION_ICONS = {
  create: <AddIcon fontSize="small" />,
  update: <EditIcon fontSize="small" />,
  delete: <DeleteIcon fontSize="small" />,
};

export const AuditLog: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterUser, setFilterUser] = useState('');
  const [filterTable, setFilterTable] = useState('all');
  const [filterOperation, setFilterOperation] = useState('all');

  // Fetch audit logs from API
  const { data: auditLogs = [], isLoading, error } = useQuery({
    queryKey: ['auditLogs', filterUser, filterTable, filterOperation, page, rowsPerPage],
    queryFn: () => listAuditLogsDirect({
      userId: filterUser || undefined,
      tableName: filterTable !== 'all' ? filterTable : undefined,
      operation: filterOperation !== 'all' ? filterOperation as any : undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getChangeSummary = (log: AuditLogEntry) => {
    if (!log.old_values && !log.new_values) return null;
    
    if (log.operation === 'create') {
      return Object.keys(log.new_values || {}).length + ' fields created';
    }
    
    if (log.operation === 'delete') {
      return 'Record deleted';
    }
    
    // For updates, show changed fields
    if (log.old_values && log.new_values) {
      const changedFields = Object.keys(log.new_values).filter(
        key => JSON.stringify(log.old_values?.[key]) !== JSON.stringify(log.new_values?.[key])
      );
      return changedFields.length > 0 ? `${changedFields.length} field(s) changed` : 'No changes';
    }
    
    return null;
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Audit Log
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Track all system changes and user activities.
          </Typography>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="User"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                fullWidth
                size="small"
                placeholder="Filter by user email"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Table</InputLabel>
                <Select
                  value={filterTable}
                  onChange={(e) => setFilterTable(e.target.value)}
                  label="Table"
                >
                  <MenuItem value="all">All Tables</MenuItem>
                  <MenuItem value="timetable_records">Timetable Records</MenuItem>
                  <MenuItem value="teachers">Teachers</MenuItem>
                  <MenuItem value="subjects">Subjects</MenuItem>
                  <MenuItem value="classes">Classes</MenuItem>
                  <MenuItem value="absences">Absences</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Operation</InputLabel>
                <Select
                  value={filterOperation}
                  onChange={(e) => setFilterOperation(e.target.value)}
                  label="Operation"
                >
                  <MenuItem value="all">All Operations</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Paper>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box py={4} px={2}>
            <Typography color="error" align="center">
              Failed to load audit logs. Please try again.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>School Context</TableCell>
                    <TableCell>Operation</TableCell>
                    <TableCell>Table</TableCell>
                    <TableCell>Record ID</TableCell>
                    <TableCell>Changes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={4}>
                          No audit log entries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {log.user_id ? log.user_id.substring(0, 8) + '...' : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {log.school_context || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={OPERATION_ICONS[log.operation]}
                            label={log.operation.toUpperCase()}
                            color={OPERATION_COLORS[log.operation]}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <code>{log.table_name}</code>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {log.record_id.substring(0, 12)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {getChangeSummary(log) || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={-1} // Unknown total count with server-side pagination
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to }) => `${from}-${to}`}
            />
          </>
        )}
      </Paper>

      <Box>
        <Typography variant="caption" color="text.secondary">
          Audit logs track all create, update, and delete operations on critical tables.
          Super Admin can see all logs across all schools. Admins can see logs for their school only.
        </Typography>
      </Box>
    </Stack>
  );
};
